import Decimal from "decimal.js";
import {
  FETCH_ERROR,
  GAS_FIRED_GENERATION,
  GRID,
  MCR_AVG_UOM,
  NATURAL_GAS,
  contactPerson,
} from "./api/constants";
import { downloadKpiData, saveKpiData } from "./api/services";
import { notify } from "./components/Toast/notify";
import { four, hundred, six, ten, three, twelve, two } from "./constants";
import type {
  ICalculatorFormState,
  ICarbonEmissionYearWise,
  IKpiContentData,
  SaveKpiDataI,
  YearWiseData,
} from "./types/types";

export const regexForOneToHundredUptoTwoDecimals = /^(100(\.0{1,2})?|[1-9]\d?(\.\d{1,2})?)$/;

// the below function is used to convert step 2 table input to comma separated number with max 12 whole digits and 6 decimal

export const convertInputToCommaSeparatedWithTwoDecimal = (input: string) => {
  let sanitizedInput = input.replace(/(?!^[.-])\D/g, ""); // Allow '-' and '.' only at the start
  // Remove any extra decimals and negative signs
  sanitizedInput = sanitizedInput.replace(/([.-])(?=.+[.-])/g, "");
  // Remove leading zeros
  sanitizedInput = sanitizedInput.replace(/^0+(?=\d)/, "");
  // Remove leading negative sign if it's not the first character
  sanitizedInput = sanitizedInput.replace(/(?!^)-/g, "");
  // remove any second decimal dot if there are two consecutive dots
  sanitizedInput = sanitizedInput.replace(/\.{2,}/g, ".");
  if (sanitizedInput === "" || isNaN(Number(sanitizedInput))) {
    return ""; // Return 0 if input is not a number or is empty
  }

  let [whole, decimal = ""] = sanitizedInput.split("."); // Split input into whole and decimal parts

  // Remove the 13th digit
  if (whole.replace(".", "").replace("-", "").length > twelve) {
    whole = whole.slice(0, -1);
  }

  // Truncate whole part to 12 digits if necessary
  if (whole.length > twelve) {
    whole = whole.slice(0, twelve);
  }

  // Truncate decimal part to 2 digits if necessary
  decimal = decimal.slice(0, six);

  let formattedInput = Number.parseFloat(whole).toLocaleString(); // Convert input to number and format as comma-separated string

  // if just a decimal dot is there, but no decimal part, then append a 0
  if (sanitizedInput.includes(".")) {
    if (decimal === "") {
      formattedInput = formattedInput + ".";
    } else {
      formattedInput = `${formattedInput} . ${decimal}`;
    }
  }
  return formattedInput; // Remove any leading negative sign from the formatted input
};

// the below function is used to convert step 2 table INPUT AS PASTED DATA to comma separated number with max 16 whole digits and 2 decimal
export const convertCopiedInputToCommaSeparatedWithTwoDecimal = (value: string) => {
  // Allow negative numbers by preserving the '-' at the start
  const cleanedInput = value.replace(/(?!^-)[^0-9.]/g, "").replace(/(\.\d{2})\d+/g, "$1");

  // Parse the cleaned input to a number, or return an empty string if it's not a valid number
  const number = Number.parseFloat(cleanedInput);
  if (isNaN(number)) {
    return "";
  }

  // Format the number with commas and up to two decimal places, with only one dot
  const parts = cleanedInput.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (parts.length > 1) {
    parts[1] = parts[1].substring(0, two).padEnd(two, "0");
    return `${parts[0]}.${parts[1]}`;
  } else {
    return parts[0];
  }
};
// the below function is used to convert step 3 table data to comma separated number upto 3 decimal
export const convertInputToCommaSeparatedWithThreeDecimal = (value: string) => {
  // Retain the negative sign at the start and remove other non-digit characters
  const cleanedInput = value.replace(/(?!^-)[^0-9.]/g, "").replace(/(\.\d{3})\d+/g, "$1");

  // Parse the cleaned input to a number, or return an empty string if it's not a valid number
  const number = Number.parseFloat(cleanedInput);
  if (isNaN(number)) {
    return "";
  }

  // Format the number with commas and up to three decimal places, with only one dot
  const parts = cleanedInput.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (parts.length > 1) {
    parts[1] = parts[1].substring(0, three).padEnd(three, "0");
    return `${parts[0]}.${parts[1]}`;
  } else {
    return parts[0];
  }
};
// Generic converter: format number string with commas and up to `decimals` decimal places
export const convertInputToCommaSeparatedWithDecimal = (value: string, decimals: number) => {
  if (decimals <= 0) return convertInputToCommaSeparatedWithThreeDecimal(value);
  const cleanedInput = value
    .replace(/(?!^-)[^0-9.]/g, "")
    .replace(new RegExp(`(\\.\\d{${decimals}})\\d+`), "$1");
  const number = Number.parseFloat(cleanedInput);
  if (isNaN(number)) return "";
  const parts = cleanedInput.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  if (parts.length > 1) {
    parts[1] = parts[1].substring(0, decimals).padEnd(decimals, "0");
    return `${parts[0]}.${parts[1]}`;
  } else {
    return parts[0];
  }
};
// the below function is to convert bigInt numbers to decimal numbers upto 3 places
export const convertBigIntToDecimal = (value: number) => {
  const PRECISION = 3;

  const splittedNumber: string[] = value.toString().split(".");
  let actualNumber: string = splittedNumber[1]
    ? `${splittedNumber[0]} . ${splittedNumber[1].substring(0, four)}`
    : splittedNumber[0];
  actualNumber = Number(actualNumber.replace(/\s/g, "")).toFixed(PRECISION).toString();
  return actualNumber;
};

// Function for download KPI deal for specific user
export const handleDownloadKpiDeal = () => {
  (async () => {
    try {
      // Make a GET request to download the CSV file from the API
      const response = await downloadKpiData();
      // Create a Blob from the response data
      const blob = new Blob([response.data], { type: "text/csv" });
      const filename = "CPM_CarbonKPICalc_DealDump";
      // Create a temporary URL for the Blob
      const url = window.URL.createObjectURL(blob);
      // Programmatically create a link
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      // Simulate a click event to initiate the download
      document.body.appendChild(link);
      link.click();
      // Cleanup: Remove the temporary URL and the link element
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch {
      notify("error", FETCH_ERROR);
    }
  })();
};
const formatDateToISOString = (dateString: string) => {
  return new Date(dateString).toISOString().slice(0, ten);
};

const getVolumeByUser = (
  state: ICalculatorFormState,
  year: number,
  currentCommodity: string,
  currentTechSrc?: string
) => {
  let userInput = new Decimal(0);
  for (const rowData of state.stepTwoTableData) {
    const commodity =
      currentCommodity === "NG" || currentCommodity === "RNG" || currentCommodity === "SLMT"
        ? state.commodity
        : currentTechSrc;
    if (rowData[state.stepTwoTableFirstCol as unknown as keyof typeof rowData] === commodity) {
      userInput = rowData[year as unknown as keyof typeof rowData] as unknown as Decimal;
      break;
    }
  }
  return userInput;
};

// Helper: read a KPI value from stepThreeTableData by label and year
// Import IKpiModal type for type assertion in Scope 1 Combustion CI save logic
import type { IKpiModal } from "./types/types";

// Helper: Extract NCI Impact value from special handling or fallback to row search
const getNCIImpactValue = (
  myState:
    | IKpiContentData
    | { stepThreeTableData?: Array<Record<string, unknown>> }
    | null
    | undefined,
  rows: Array<Record<string, unknown>>,
  year: number | string
): string | number | null => {
  if (
    Array.isArray(rows) &&
    rows.length === 0 &&
    typeof (myState as unknown as IKpiContentData).nciImpactYearWise === "object"
  ) {
    const nciImpactYearWise = (myState as unknown as IKpiContentData).nciImpactYearWise as Record<
      number,
      string | number
    >;
    const yearNum = typeof year === "string" ? Number.parseInt(year, 10) : year;
    if (nciImpactYearWise && Object.prototype.hasOwnProperty.call(nciImpactYearWise, yearNum)) {
      return nciImpactYearWise[yearNum];
    }
  }

  const row = rows.find((r) => {
    const key = String(r["KPI"] ?? "")
      .toLowerCase()
      .trim();
    return key.includes("nci impact");
  });
  if (!row) return null;

  const val = row[String(year)];
  if (val === undefined || val === null) return null;
  if (typeof val === "string") {
    const s = val.trim();
    if (s === "" || s === "-") return null;
    return s as string;
  }
  return val as number;
};

const getStepThreeValue = (
  myState:
    | IKpiContentData
    | { stepThreeTableData?: Array<Record<string, unknown>> }
    | null
    | undefined,
  label: string,
  year: number | string
) => {
  // Type guard: ensure object has stepThreeTableData
  const hasStepThree = (
    obj: unknown
  ): obj is { stepThreeTableData?: Array<Record<string, unknown>> } => {
    return typeof obj === "object" && obj !== null && "stepThreeTableData" in obj;
  };

  if (!myState || !hasStepThree(myState)) return null;
  const rows = myState.stepThreeTableData as Array<Record<string, unknown>> | undefined;
  if (!rows) return null;

  // Special handling for NCI Impact
  if (label.toLowerCase().includes("nci impact")) {
    return getNCIImpactValue(myState, rows, year);
  }

  // Default handling for other labels
  const row = rows.find((r) => {
    const key = String(r["KPI"] ?? "")
      .toLowerCase()
      .trim();
    return key.includes(label.toLowerCase().trim());
  });
  if (!row) return null;
  const val = row[String(year)];
  if (val === undefined || val === null) return null;
  if (typeof val === "string") {
    const s = val.trim();
    if (s === "" || s === "-") return null;
    return s as string;
  }
  return val as number;
};

// Helper: Apply summary KPIs from card state
const applySummaryKpiMappings = (kpiObj: SaveKpiDataI, kpiState: IKpiContentData) => {
  if (kpiState.equityScope12PeakEmission !== undefined) {
    kpiObj.EQUITY_SCOPE_PEAK_EMISSION = kpiState.equityScope12PeakEmission;
  }
  if (kpiState.netAbsEmsPeak !== undefined) {
    kpiObj.NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION = kpiState.netAbsEmsPeak;
  }
  if (kpiState.mcrAvg !== undefined) {
    kpiObj.MARGIN_TO_CARBON_RATIO_AVG = kpiState.mcrAvg;
  }
};

// Helper: Apply year-wise KPIs from stepThreeTableData
const applyYearWiseKpiMappings = (
  kpiObj: SaveKpiDataI,
  kpiState: IKpiContentData,
  year: number | string
) => {
  const nciImpactYearWise = kpiState.nciImpactYearWise || {};
  const operated = getStepThreeValue(kpiState, "operated scope", year);
  const equity = getStepThreeValue(kpiState, "equity scope", year);
  const netAbs =
    getStepThreeValue(kpiState, "net absolute emissions", year) ||
    getStepThreeValue(kpiState, "net absolute emission", year);
  const avgCi =
    getStepThreeValue(kpiState, "avg carbon intensity", year) ||
    getStepThreeValue(kpiState, "average carbon intensity", year);

  kpiObj.OPERATED_SCOPE = operated ?? kpiObj.OPERATED_SCOPE ?? null;
  kpiObj.OPERATED_SCOPE_UNIT = operated ? "MTPA" : (kpiObj.OPERATED_SCOPE_UNIT ?? "");
  kpiObj.EQUITY_SCOPE = equity ?? kpiObj.EQUITY_SCOPE ?? null;
  kpiObj.EQUITY_SCOPE_UNIT = equity ? "MTPA" : (kpiObj.EQUITY_SCOPE_UNIT ?? "");
  kpiObj.NET_ABSOLUTE_EMISSION = netAbs ?? kpiObj.NET_ABSOLUTE_EMISSION ?? null;
  kpiObj.AVG_CARBON_INTENSITY = avgCi ?? kpiObj.AVG_CARBON_INTENSITY ?? null;
  kpiObj.NCI_IMPACT =
    nciImpactYearWise[year as keyof typeof nciImpactYearWise] !== undefined
      ? nciImpactYearWise[year as keyof typeof nciImpactYearWise]
      : "NA";
  kpiObj.NCI_IMPACT_2026 = nciImpactYearWise[2026] !== undefined ? nciImpactYearWise[2026] : "NA";
  kpiObj.NCI_IMPACT_2030 = nciImpactYearWise[2030] !== undefined ? nciImpactYearWise[2030] : "NA";
};

// Helper: Apply T&S Product specific KPIs
const applyTSProductKpiMappings = (
  kpiObj: SaveKpiDataI,
  kpiState: IKpiContentData,
  year: number | string
) => {
  const volWeightedAvgCI =
    getStepThreeValue(kpiState, "volume-weighted avg carbon intensity", year) ||
    getStepThreeValue(kpiState, "volume-weighted", year) ||
    getStepThreeValue(kpiState, "volume weighted", year) ||
    null;
  const scope3Cat11Emission =
    getStepThreeValue(kpiState, "scope 3 category 11 emission", year) ||
    getStepThreeValue(kpiState, "scope 3 cat 11 emission", year) ||
    null;
  kpiObj.VOL_WEIGHTED_AVG_CI = volWeightedAvgCI;

  if (typeof scope3Cat11Emission === "number") {
    kpiObj.SCOPE3_CAT11_EMISSION = scope3Cat11Emission;
  } else if (typeof scope3Cat11Emission === "string") {
    const cleaned = scope3Cat11Emission.trim().replace(/,/g, "");
    kpiObj.SCOPE3_CAT11_EMISSION =
      cleaned !== "" && !isNaN(Number(cleaned)) ? Number(cleaned) : null;
  } else {
    kpiObj.SCOPE3_CAT11_EMISSION = null;
  }
};

// Helper: Apply Scope 1 Combustion CI mappings
const applyScopeCombustionCIMappings = (
  kpiObj: SaveKpiDataI,
  kpiState: IKpiContentData,
  year: number | string
) => {
  let scopeCombustionCI = null;
  try {
    const valFromObj =
      kpiState?.scope1CmbCrbnIntYearWiseObj?.[
        year as keyof typeof kpiState.scope1CmbCrbnIntYearWiseObj
      ];
    if (valFromObj !== undefined && valFromObj !== null && String(valFromObj).trim() !== "") {
      scopeCombustionCI = valFromObj;
    } else if (
      Array.isArray(kpiState.stepThreeTableData) &&
      kpiState.stepThreeTableData.length > 0
    ) {
      const row = (kpiState.stepThreeTableData as unknown as IKpiModal[]).find((r) =>
        (r.KPI || "").toLowerCase().includes("scope 1 combustion carbon intensity")
      );
      const rowVal = row && row[year as keyof typeof row];
      if (rowVal !== undefined && rowVal !== null && String(rowVal).trim() !== "") {
        scopeCombustionCI = rowVal;
      }
    }
  } catch {
    // ignore errors resolving scope combustion CI
  }
  kpiObj.SCOPE_COMBUSTION_CI = scopeCombustionCI;
};

// --- Common KPI Mappings: UI to Payload ---
export const applyCommonKpiMappings = (
  kpiObj: SaveKpiDataI,
  state: ICalculatorFormState,
  kpiState: IKpiContentData,
  year: number | string
) => {
  applySummaryKpiMappings(kpiObj, kpiState);
  applyYearWiseKpiMappings(kpiObj, kpiState, year);
  applyTSProductKpiMappings(kpiObj, kpiState, year);
  applyScopeCombustionCIMappings(kpiObj, kpiState, year);
};
// (removed duplicate, out-of-scope block)

const cleanupPayloadByEvpType = (saveKpiDataObj: SaveKpiDataI[]): SaveKpiDataI[] => {
  return saveKpiDataObj.map((record) => {
    const isTSProducts = record.EVP_NAME?.toLowerCase().replace(/[^a-z0-9]/g, "") === "tsproducts";

    if (isTSProducts) {
      // For T&S Products: keep PRODUCT_ID, remove TRADE_COMMODITY_ID
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { TRADE_COMMODITY_ID, ...restRecord } = record;
      return restRecord as SaveKpiDataI;
    } else {
      // For other EVPs: keep TRADE_COMMODITY_ID, remove PRODUCT_ID
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { PRODUCT_ID, ...restRecord } = record;
      return restRecord as SaveKpiDataI;
    }
  });
};

export const handleSaveDeal = async (
  state: ICalculatorFormState,
  kpiState: IKpiContentData,
  userName: string
) => {
  // write api to save data to DB

  const kpiDataDefaultObject: SaveKpiDataI = getDefaultKpiObject(state, userName);
  const saveKpiDataObj: SaveKpiDataI[] = [kpiDataDefaultObject];
  if (!isNaN(state.mcrAverage) && isFinite(state.mcrAverage)) {
    kpiDataDefaultObject.MCR = state.mcrAverage;
  }

  // Call per-commodity handlers which will populate year-wise objects and push to saveKpiDataObj
  setKpiObjectForPipelineGas(kpiDataDefaultObject, state, kpiState, saveKpiDataObj);
  setKpiObjectForPower(kpiDataDefaultObject, state, kpiState, saveKpiDataObj);
  setKpiObjectForRng(kpiDataDefaultObject, state, kpiState, saveKpiDataObj);
  setKpiObjectForTSProducts(kpiDataDefaultObject, state, kpiState, saveKpiDataObj);
  setKpiObjectForSLMT(kpiDataDefaultObject, state, kpiState, saveKpiDataObj);
  // remove first object which is a dummy
  saveKpiDataObj.shift();

  // Clean up payload to remove PRODUCT_ID for non-T&S Products and vice versa
  const cleanedPayload = cleanupPayloadByEvpType(saveKpiDataObj);

  // Save payload prepared; logging centralized in `logKpiMappingInputs` helper.
  // make api to save data to db
  try {
    await saveKpiData(cleanedPayload);
  } catch {
    notify("error", FETCH_ERROR);
  }
};

const getDefaultKpiObject = (state: ICalculatorFormState, userName: string) => {
  const evpStr = state.evp?.toLowerCase().replace(/[^a-z0-9]/g, "");

  return {
    EVP_NAME: state.evp, // Save the EVP name (Shell Energy, DR Power, T&S Products, SLMT)
    EVP_ID: state.evpId || null, // Save the EVP ID
    OPPORTUNITY_NAME: state.opportunity,
    TRADE_COMMODITY_ID: Array.isArray(state.commodityId) ? 0 : Number(state.commodityId),
    PRODUCT_ID: null, // Will be set for T&S Products
    TECHNOLOGY_SOURCE_ID: null, // each object for a technology source, NULL if it is gas fired generation.
    COUNTRY_ID: evpStr === "slmt" ? 0 : Number(state.countryId),
    COUNTRY_ASSET_OF_SUPPLY_ID: evpStr === "slmt" ? Number(state.countryId) : null,
    GRID_ID: null, // grid location id. NULL if technology source is not GRID
    BUSINESS_ID: state.businessId,
    GROSS_MARGIN: state.grossMargin,
    CURRENCY: "USD", // unit of measure of Gross margin
    START_DATE: formatDateToISOString(state.startDate),
    END_DATE: formatDateToISOString(state.endDate),
    YEAR: state.startYear, // will iterate for all years
    QUANTITY: "0", // user entered volume for the technology source and Year, to be iterated
    QUANTITY_UNIT: state.uomVal, // UOM to be picked from form input
    FUEL_TYPE: "", // NULL if technology source is not Gas Fired Generation
    ASSET_TYPE: "", // NULL if commodity is not power and technology source is not Gas fired generation.
    OPERATING_MODEL: "", // for Gas Fired Generation
    EQUITY_SHARE: Number(state.equityShare) * hundred, // from form
    EFFICIENCY: Number(state.efficiency) * hundred, // from form
    CARBON_EMISSIONS: 1.0, // carbon emission for that technology source and Year, PVCE*volume*UOM for Gas Fired Generation
    CARBON_EMISSIONS_UNIT: "MTPA",
    CARBON_INTENSITY: 1.0, // carbon intensity for that technology source and Year, PVCE for Gas Fired Generation
    CARBON_INTENSITY_UNIT: "", // check with backend to receive in CI api
    DEALMAKER_USERID: userName.toLowerCase(), // singed in user,
    CI_EFL_last_update_timestamp: "", // the time stamp is received for each Carbon Intensity Api calls in InputTable component
    Power_Performance_Standards: "", // the power performance standard messages
    UOM: MCR_AVG_UOM, // the UOM for MCR average calculated
    IS_OPERATING_PLAN: state.operatingPlan,
    TYPE_OF_SUPPLY: state.typeOfSupply || "", // Type of Supply for SLMT only
    CARBON_CREDIT: state.carbonCredit, // For T&S Products only: 0 = No, 1 = Yes
    CARBON_CREDIT_QUANTITY: 0, // Will be set for T&S Products with carbon credits
    // Business-friendly KPI keys (default to null or '')
    EQUITY_SCOPE_PEAK_EMISSION: null,
    NAE_SCOPE123_PEAK_EMISSION: null,
    MARGIN_TO_CARBON_RATIO_AVG: null,
    NCI_IMPACT_2026: null,
    NCI_IMPACT_2030: null,
    OPERATED_SCOPE: null,
    OPERATED_SCOPE_UNIT: "MTPA",
    EQUITY_SCOPE: null,
    EQUITY_SCOPE_UNIT: "MTPA",
    NET_ABSOLUTE_EMISSION: null,
    AVG_CARBON_INTENSITY: null,
    VOL_WEIGHTED_AVG_CI: null,
  };
};

const setKpiObjectForPipelineGas = (
  kpiDataDefaultObject: SaveKpiDataI,
  state: ICalculatorFormState,
  myState: IKpiContentData,
  saveKpiDataObj: SaveKpiDataI[]
) => {
  // for commodity pipeline gas
  const commodityStr = Array.isArray(state.commodity) ? state.commodity[0] : state.commodity;
  if (commodityStr?.replaceAll(" ", "").toLocaleLowerCase() === "pipelinegas") {
    for (let year = state.startYear; year < state.endYear + 1; year++) {
      // set irrelevant parameters to null or empty
      kpiDataDefaultObject.TECHNOLOGY_SOURCE_ID = null;
      kpiDataDefaultObject.GRID_ID = null;
      kpiDataDefaultObject.FUEL_TYPE = "";
      kpiDataDefaultObject.OPERATING_MODEL = "";
      kpiDataDefaultObject.EQUITY_SHARE = null;
      kpiDataDefaultObject.EFFICIENCY = null;
      kpiDataDefaultObject.OPERATED_SCOPE = null;
      kpiDataDefaultObject.OPERATED_SCOPE_UNIT = "";
      kpiDataDefaultObject.EQUITY_SCOPE = null;
      kpiDataDefaultObject.EQUITY_SCOPE_UNIT = "";
      kpiDataDefaultObject.ASSET_TYPE = "";
      kpiDataDefaultObject.Power_Performance_Standards = "";
      // set year
      kpiDataDefaultObject.YEAR = year;
      const volumeByUserForThisTechSrcAndThisYear = getVolumeByUser(state, year, "NG");
      // set quantity
      kpiDataDefaultObject.QUANTITY = volumeByUserForThisTechSrcAndThisYear
        .toString()
        .replaceAll(",", "");
      // set carbon emission
      kpiDataDefaultObject.CARBON_EMISSIONS = state.carbonEmissionYearWise[0][year];
      // set carbon intensity
      kpiDataDefaultObject.CARBON_INTENSITY = state.carbonIntensityYearWise[0][year];
      // set carbon intensity UOM
      kpiDataDefaultObject.CARBON_INTENSITY_UNIT = state.carbonIntensityUomYearWise[0][
        year
      ] as string;
      // set Time Stamp Data
      kpiDataDefaultObject.CI_EFL_last_update_timestamp = state.timeStampDataYearWise[0][
        year
      ] as string;
      // Assign calculated KPIs from myState (IKpiContentData)
      if (myState.mcrAvg !== undefined) {
        kpiDataDefaultObject.MCR = Number(myState.mcrAvg);
      }
      if (myState.equityScopeDataYearWise && myState.equityScopeDataYearWise[year] !== undefined) {
        kpiDataDefaultObject.EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION =
          myState.equityScopeDataYearWise[year];
      }
      if (myState.netAbsEmsDataYearWise && myState.netAbsEmsDataYearWise[year] !== undefined) {
        kpiDataDefaultObject.NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION =
          myState.netAbsEmsDataYearWise[year];
      }
      // Add other KPIs from myState as needed (e.g., scope1CmbCrbnIntYearWiseObj, etc.)
      // save object for this year
      // apply common mappings from KPI UI (step 3) into the payload
      applyCommonKpiMappings(kpiDataDefaultObject, state, myState, year);
      const finalKpiData: SaveKpiDataI = { ...kpiDataDefaultObject };
      saveKpiDataObj.push(finalKpiData);
    }
  }
};

const setKpiObjectForPower = (
  kpiDataDefaultObject: SaveKpiDataI,
  state: ICalculatorFormState,
  myState: IKpiContentData,
  saveKpiDataObj: SaveKpiDataI[]
) => {
  // for commodity as power
  const commodityStr = Array.isArray(state.commodity) ? state.commodity[0] : state.commodity;
  if (commodityStr?.toLowerCase() === "power") {
    for (let techSrc = 0; techSrc < state.technologySource.length; techSrc++) {
      const currentTechSrc = state.selectedTechnologySources[techSrc].value;
      kpiDataDefaultObject.TECHNOLOGY_SOURCE_ID = state.selectedTechnologySources[techSrc].id;
      setGridId(currentTechSrc, kpiDataDefaultObject, state);
      for (let year = state.startYear; year < state.endYear + 1; year++) {
        kpiDataDefaultObject.YEAR = year;
        const volumeByUserForThisTechSrcAndThisYear = getVolumeByUser(
          state,
          year,
          "PW",
          currentTechSrc
        );
        // set quantity
        kpiDataDefaultObject.QUANTITY = volumeByUserForThisTechSrcAndThisYear
          .toString()
          .replaceAll(",", "");
        getCarbonEmissionYearWise(
          state.carbonEmissionYearWise,
          currentTechSrc,
          year,
          kpiDataDefaultObject
        );
        getCarbonIntYearWiseForNonGfpTechSrc(
          state.carbonIntensityYearWise,
          state.carbonIntensityUomYearWise,
          state.timeStampDataYearWise,
          currentTechSrc,
          year,
          kpiDataDefaultObject
        );
        // attributes for Gas Fired Generation
        kpiDataDefaultObject.Power_Performance_Standards = "";
        setKpiDefaultObjForGFP(currentTechSrc, state, kpiDataDefaultObject, year);
        // save object for this year
        // apply common mappings from KPI UI (step 3) into the payload
        applyCommonKpiMappings(kpiDataDefaultObject, state, myState, year);
        const finalKpiData: SaveKpiDataI = { ...kpiDataDefaultObject };
        saveKpiDataObj.push(finalKpiData);
      }
    }
  }
};

const setKpiObjectForRng = (
  kpiDataDefaultObject: SaveKpiDataI,
  state: ICalculatorFormState,
  kpiState: IKpiContentData,
  saveKpiDataObj: SaveKpiDataI[]
) => {
  // for commodity RNG
  const commodityStr = Array.isArray(state.commodity) ? state.commodity[0] : state.commodity;
  if (commodityStr?.replaceAll(" ", "").toLocaleLowerCase() === "rng") {
    for (let year = state.startYear; year < state.endYear + 1; year++) {
      // set irrelevant parameters to null or empty
      kpiDataDefaultObject.TECHNOLOGY_SOURCE_ID = null;
      kpiDataDefaultObject.GRID_ID = null;

      kpiDataDefaultObject.FUEL_TYPE = "";
      kpiDataDefaultObject.OPERATING_MODEL = "";
      kpiDataDefaultObject.EQUITY_SHARE = null;
      kpiDataDefaultObject.EFFICIENCY = null;
      kpiDataDefaultObject.OPERATED_SCOPE = null;
      kpiDataDefaultObject.OPERATED_SCOPE_UNIT = "";
      kpiDataDefaultObject.EQUITY_SCOPE = null;
      kpiDataDefaultObject.EQUITY_SCOPE_UNIT = "";
      kpiDataDefaultObject.ASSET_TYPE = "";
      kpiDataDefaultObject.Power_Performance_Standards = "";

      // set year
      kpiDataDefaultObject.YEAR = year;
      const volumeByUserForThisTechSrcAndThisYear = getVolumeByUser(state, year, "RNG");
      // set quantity
      kpiDataDefaultObject.QUANTITY = volumeByUserForThisTechSrcAndThisYear
        .toString()
        .replaceAll(",", "");
      // set carbon emission
      kpiDataDefaultObject.CARBON_EMISSIONS = state.carbonEmissionYearWise[0][year];
      // set carbon intensity
      kpiDataDefaultObject.CARBON_INTENSITY = state.carbonIntensityYearWise[0][year];
      // set carbon intensity UOM
      kpiDataDefaultObject.CARBON_INTENSITY_UNIT = state.carbonIntensityUomYearWise[0][
        year
      ] as string;
      // set Time Stamp Data
      kpiDataDefaultObject.CI_EFL_last_update_timestamp = state.timeStampDataYearWise[0][
        year
      ] as string;
      // Assign calculated KPIs from kpiState (IKpiContentData)
      if (kpiState.mcrAvg !== undefined) {
        kpiDataDefaultObject.MCR = Number(kpiState.mcrAvg);
      }
      if (
        kpiState.equityScopeDataYearWise &&
        kpiState.equityScopeDataYearWise[year] !== undefined
      ) {
        kpiDataDefaultObject.EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION =
          kpiState.equityScopeDataYearWise[year];
      }
      if (kpiState.netAbsEmsDataYearWise && kpiState.netAbsEmsDataYearWise[year] !== undefined) {
        kpiDataDefaultObject.NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION =
          kpiState.netAbsEmsDataYearWise[year];
      }
      // Add other KPIs from kpiState as needed
      // save object for this year
      // apply common mappings from KPI UI (step 3) into the payload
      applyCommonKpiMappings(kpiDataDefaultObject, state, kpiState, year);
      const finalKpiData: SaveKpiDataI = { ...kpiDataDefaultObject };
      saveKpiDataObj.push(finalKpiData);
    }
  }
};

const resetNonPowerFields = (kpiDataDefaultObject: SaveKpiDataI) => {
  kpiDataDefaultObject.TECHNOLOGY_SOURCE_ID = null;
  kpiDataDefaultObject.GRID_ID = null;
  kpiDataDefaultObject.FUEL_TYPE = "";
  kpiDataDefaultObject.OPERATING_MODEL = "";
  kpiDataDefaultObject.EQUITY_SHARE = null;
  kpiDataDefaultObject.EFFICIENCY = 0;
  kpiDataDefaultObject.OPERATED_SCOPE = null;
  kpiDataDefaultObject.OPERATED_SCOPE_UNIT = "";
  kpiDataDefaultObject.EQUITY_SCOPE = null;
  kpiDataDefaultObject.EQUITY_SCOPE_UNIT = "";
  kpiDataDefaultObject.ASSET_TYPE = "";
  kpiDataDefaultObject.Power_Performance_Standards = "";
};

const getTSProductId = (product: string, state: ICalculatorFormState) => {
  if (Array.isArray(state.commodity) && Array.isArray(state.commodityId)) {
    const productIndex = state.commodity.findIndex(
      (item) => String(item).toLowerCase() === String(product).toLowerCase()
    );
    return Number(state.commodityId[productIndex] ?? 0);
  }
  return Array.isArray(state.commodityId)
    ? Number(state.commodityId[0] ?? 0)
    : Number(state.commodityId);
};

// Removed unused getProductCarbonIntensityData function as per error report

// Removed unused setCommonYearFields function as per error report

// Helper: Set carbon intensity unit for TS Products
const setProductCarbonIntensityUnitForTS = (
  kpiData: SaveKpiDataI,
  state: ICalculatorFormState,
  product: any
) => {
  if (!state.productCarbonIntensities || state.productCarbonIntensities.length === 0) {
    return;
  }
  const productId = kpiData.PRODUCT_ID;
  const productName = typeof product === "string" ? product : "";
  let match = state.productCarbonIntensities.find((p) => (p as any).PRODUCT_ID === productId);
  if (!match) {
    match = state.productCarbonIntensities.find(
      (p) => p.PRODUCT_NAME?.toLowerCase() === productName.toLowerCase()
    );
  }
  if (match && match.UNIT_CODE) {
    kpiData.CARBON_INTENSITY_UNIT = match.UNIT_CODE;
  }
};

// Helper: Set carbon credit quantity
const setCarbonCreditQuantityForTS = (
  kpiData: SaveKpiDataI,
  state: ICalculatorFormState,
  year: number
) => {
  if (state.carbonCredit === 1) {
    try {
      const carbonCreditsRow = (state.stepTwoTableData || []).find((row) =>
        String(row[state.stepTwoTableFirstCol as unknown as keyof typeof row] ?? "")
          .toLowerCase()
          .includes("carbon credit")
      );
      const ccVal = carbonCreditsRow
        ? carbonCreditsRow[year as unknown as keyof typeof carbonCreditsRow]
        : 0;
      const ccNumber =
        typeof ccVal === "number" ? ccVal : Number((ccVal || "").toString().replace(/,/g, "")) || 0;
      kpiData.CARBON_CREDIT_QUANTITY = ccNumber;
    } catch {
      kpiData.CARBON_CREDIT_QUANTITY = 0;
    }
  } else {
    kpiData.CARBON_CREDIT_QUANTITY = 0;
  }
};

// Helper: Set user quantity for TS Products
const setUserQuantityForTS = (
  kpiData: SaveKpiDataI,
  state: ICalculatorFormState,
  year: number,
  product: any
) => {
  try {
    const volumeByUserForThisCommodityAndThisYear = getVolumeByUser(state, year, "TS", product);
    kpiData.QUANTITY = volumeByUserForThisCommodityAndThisYear.toString().replaceAll(",", "");
  } catch {
    kpiData.QUANTITY = (kpiData.QUANTITY ?? "0").toString();
  }
};

// Helper: Process single year's KPI data for TS Products
const processYearKpiForTSProducts = (
  kpiDataDefaultObject: SaveKpiDataI,
  state: ICalculatorFormState,
  kpiState: IKpiContentData,
  businessId: any,
  product: any,
  year: number
) => {
  resetNonPowerFields(kpiDataDefaultObject);
  kpiDataDefaultObject.BUSINESS_ID = businessId;
  kpiDataDefaultObject.PRODUCT_ID = getTSProductId(product, state);
  kpiDataDefaultObject.TRADE_COMMODITY_ID = 0;
  kpiDataDefaultObject.YEAR = year;

  if (kpiState.mcrAvg !== undefined) {
    kpiDataDefaultObject.MCR = Number(kpiState.mcrAvg);
  }
  if (kpiState.equityScopeDataYearWise && kpiState.equityScopeDataYearWise[year] !== undefined) {
    kpiDataDefaultObject.EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION =
      kpiState.equityScopeDataYearWise[year];
  }
  if (kpiState.netAbsEmsDataYearWise && kpiState.netAbsEmsDataYearWise[year] !== undefined) {
    kpiDataDefaultObject.NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION =
      kpiState.netAbsEmsDataYearWise[year];
  }

  setProductCarbonIntensityUnitForTS(kpiDataDefaultObject, state, product);
  applyCommonKpiMappings(kpiDataDefaultObject, state, kpiState, year);

  if (
    kpiState.scope1CmbCrbnIntYearWiseObj &&
    kpiState.scope1CmbCrbnIntYearWiseObj[year] !== undefined
  ) {
    kpiDataDefaultObject.SCOPE_COMBUSTION_CI = kpiState.scope1CmbCrbnIntYearWiseObj[year];
  }

  setUserQuantityForTS(kpiDataDefaultObject, state, year, product);
  setCarbonCreditQuantityForTS(kpiDataDefaultObject, state, year);

  if (kpiState.netAbsEmsPeak !== undefined) {
    kpiDataDefaultObject.NAE_SCOPE123_PEAK_EMISSION = kpiState.netAbsEmsPeak;
  }
};

export const setKpiObjectForTSProducts = (
  kpiDataDefaultObject: SaveKpiDataI,
  state: ICalculatorFormState,
  kpiState: IKpiContentData,
  saveKpiDataObj: SaveKpiDataI[]
) => {
  // for EVP T&S Products
  const evpStr = state.evp?.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (evpStr !== "tsproducts") {
    return;
  }

  // T&S Products can have multiple commodities/products and multiple businesses
  const commodityArray = Array.isArray(state.commodity) ? state.commodity : [state.commodity];
  const businessIdArray = Array.isArray(state.businessId) ? state.businessId : [state.businessId];
  for (const businessId of businessIdArray) {
    for (const product of commodityArray) {
      for (let year = state.startYear; year < state.endYear + 1; year++) {
        processYearKpiForTSProducts(
          kpiDataDefaultObject,
          state,
          kpiState,
          businessId,
          product,
          year
        );
        const finalKpiData: SaveKpiDataI = { ...kpiDataDefaultObject };
        saveKpiDataObj.push(finalKpiData);
      }
    }
  }
};

// Helper: Set irrelevant SLMT parameters to null/empty
const initializeSLMTKpiDefaults = (kpiObj: SaveKpiDataI): void => {
  kpiObj.TECHNOLOGY_SOURCE_ID = null;
  kpiObj.GRID_ID = null;
  kpiObj.FUEL_TYPE = "";
  kpiObj.OPERATING_MODEL = "";
  kpiObj.EQUITY_SHARE = null;
  kpiObj.EFFICIENCY = null;
  kpiObj.OPERATED_SCOPE = null;
  kpiObj.OPERATED_SCOPE_UNIT = "";
  kpiObj.EQUITY_SCOPE = null;
  kpiObj.EQUITY_SCOPE_UNIT = "";
  kpiObj.ASSET_TYPE = "";
  kpiObj.Power_Performance_Standards = "";
  kpiObj.CARBON_INTENSITY_UNIT = "gCO2e/MJ";
  kpiObj.CI_EFL_last_update_timestamp = "";
};

// Helper: Set KPI values from calculated state
const setSLMTCalculatedKpiValues = (
  kpiObj: SaveKpiDataI,
  kpiState: IKpiContentData,
  year: number
): void => {
  if (kpiState.mcrAvg !== undefined) {
    kpiObj.MCR = Number(kpiState.mcrAvg);
  }
  if (kpiState.equityScopeDataYearWise?.[year] !== undefined) {
    kpiObj.EQUITY_SCOPE_CARBON_PEAK_ANNUAL_EMISSION = kpiState.equityScopeDataYearWise[year];
  }
  if (kpiState.netAbsEmsDataYearWise?.[year] !== undefined) {
    kpiObj.NET_ABS_EMISSION_SCOPE_PEAK_ANNUAL_EMISSION = kpiState.netAbsEmsDataYearWise[year];
  }
};

const setKpiObjectForSLMT = (
  kpiDataDefaultObject: SaveKpiDataI,
  state: ICalculatorFormState,
  kpiState: IKpiContentData,
  saveKpiDataObj: SaveKpiDataI[]
) => {
  const evpStr = state.evp?.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (evpStr !== "slmt") return;

  for (let year = state.startYear; year < state.endYear + 1; year++) {
    initializeSLMTKpiDefaults(kpiDataDefaultObject);
    kpiDataDefaultObject.YEAR = year;
    kpiDataDefaultObject.QUANTITY = getVolumeByUser(state, year, "SLMT")
      .toString()
      .replaceAll(",", "");
    kpiDataDefaultObject.CARBON_EMISSIONS = state.carbonEmissionYearWise[0]?.[year] ?? 0;
    setSLMTCalculatedKpiValues(kpiDataDefaultObject, kpiState, year);
    applyCommonKpiMappings(kpiDataDefaultObject, state, kpiState, year);
    saveKpiDataObj.push({ ...kpiDataDefaultObject });
  }
};

const setGridId = (
  currentTechSrc: string,
  kpiDataDefaultObject: SaveKpiDataI,
  state: ICalculatorFormState
) => {
  if (currentTechSrc === GRID) {
    kpiDataDefaultObject.GRID_ID = state.gridLocationId;
  } else {
    kpiDataDefaultObject.GRID_ID = null;
  }
};

const getCarbonEmissionYearWise = (
  carbonEmissionYearWise: YearWiseData[],
  currentTechSrc: string,
  year: number,
  kpiDataDefaultObject: SaveKpiDataI
) => {
  // get carbon emission year wise
  let carbonEmissionForThisYearAndThisTechSource = 0;
  for (const element of carbonEmissionYearWise) {
    const emissionObj: ICarbonEmissionYearWise = element as ICarbonEmissionYearWise;
    if (emissionObj.techSource === currentTechSrc) {
      carbonEmissionForThisYearAndThisTechSource = Number.parseFloat(emissionObj[year]);
      break;
    }
  }
  kpiDataDefaultObject.CARBON_EMISSIONS = carbonEmissionForThisYearAndThisTechSource;
};

const getCarbonIntYearWiseForNonGfpTechSrc = (
  carbonIntensityYearWise: string | YearWiseData[],
  carbonIntensityUomYearWise: string | YearWiseData[],
  timeStampDataYearWise: string | YearWiseData[],
  currentTechSrc: string,
  year: number,
  kpiDataDefaultObject: SaveKpiDataI
) => {
  // get carbon intensity year wise for non-GFP tech sources
  let carbonIntensityForThisYearAndThisTechSource = 0;
  let carbonIntensityUomForThisYearAndThisTechSource = "";
  let timeStampDataForThisYearAndThisTechSource = "";
  for (let ciIntensityObj = 0; ciIntensityObj < carbonIntensityYearWise.length; ciIntensityObj++) {
    const ciObj: ICarbonEmissionYearWise = carbonIntensityYearWise[
      ciIntensityObj
    ] as ICarbonEmissionYearWise;
    const ciUomObj: ICarbonEmissionYearWise = carbonIntensityUomYearWise[
      ciIntensityObj
    ] as ICarbonEmissionYearWise;
    const timeStampObj: ICarbonEmissionYearWise = timeStampDataYearWise[
      ciIntensityObj
    ] as ICarbonEmissionYearWise;
    if (ciObj.techSource === currentTechSrc) {
      carbonIntensityForThisYearAndThisTechSource = Number.parseFloat(ciObj[year]);
      carbonIntensityUomForThisYearAndThisTechSource = ciUomObj[year];
      timeStampDataForThisYearAndThisTechSource = timeStampObj[year];
      break;
    }
  }
  kpiDataDefaultObject.CARBON_INTENSITY = carbonIntensityForThisYearAndThisTechSource;
  kpiDataDefaultObject.CARBON_INTENSITY_UNIT = carbonIntensityUomForThisYearAndThisTechSource;
  kpiDataDefaultObject.CI_EFL_last_update_timestamp = timeStampDataForThisYearAndThisTechSource;
};

const setKpiDefaultObjForGFP = (
  currentTechSrc: string,
  state: ICalculatorFormState,
  kpiDataDefaultObject: SaveKpiDataI,
  year: number
) => {
  // As Pipeline Gas is not valid for Power and Gas Fired Generation tech source, we need to check for Natural Gas.
  const isGasFiredPower = [NATURAL_GAS, "LNG", GAS_FIRED_GENERATION].includes(currentTechSrc);

  if (isGasFiredPower) {
    kpiDataDefaultObject.FUEL_TYPE = state.fuelType;
    kpiDataDefaultObject.OPERATING_MODEL = state.operatingModel;
    kpiDataDefaultObject.EQUITY_SCOPE = Number(
      state.equityScopeYearWiseObj[year].toString().replaceAll(",", "")
    );
    kpiDataDefaultObject.OPERATED_SCOPE = Number(
      state.operatedScopeYearWiseObj[year].toString().replaceAll(",", "")
    );
    kpiDataDefaultObject.CARBON_INTENSITY = state.carbonIntensityForGasFiredPowerYearWise[year];
    kpiDataDefaultObject.CARBON_INTENSITY_UNIT = "gCO2e/KWH";
    kpiDataDefaultObject.ASSET_TYPE = state.assetType;
    kpiDataDefaultObject.CI_EFL_last_update_timestamp =
      state.timeStampDataForGasFiredPowerYearWise[year];
    kpiDataDefaultObject.Power_Performance_Standards =
      state.powerPerformanceStandardKpiMessage.replace(/<\/?strong>/g, "");
  } else {
    // Explicitly clear Gas Fired Generation fields for non-Gas Fired technologies
    kpiDataDefaultObject.FUEL_TYPE = "";
    kpiDataDefaultObject.OPERATING_MODEL = "";
    kpiDataDefaultObject.ASSET_TYPE = "";
    kpiDataDefaultObject.EQUITY_SCOPE = null;
    kpiDataDefaultObject.OPERATED_SCOPE = null;
    kpiDataDefaultObject.Power_Performance_Standards = "";
  }
};

// Function to replace email addresses with clickable email links
export const attachEmailLinkToText = (message: string) => {
  const regex = /([\w.-]+@[\w.-]+)/g;
  if (message) {
    return message.split(regex).map((part: string, index: number) => {
      if (index % two === 0) {
        return part; // Non-email text
      } else {
        const email: string = part.trim();
        const name: string = contactPerson.filter((person) => person.email.includes(email))[0].name;
        return (
          <a key={email} href={`mailto:${email}`}>
            {name}
          </a>
        );
      }
    });
  } else {
    return "";
  }
};

export const containsHTMLTags = (inputString: string) => {
  const htmlTagPattern = /<[^>]*>/;
  return htmlTagPattern.test(inputString);
};

export const getLowerCase = (value: string) => {
  return value?.toLowerCase();
};
